// Self-contained support chat widget. Loads on Прошка (same-origin) and on the
// Tilda landing (cross-origin via /support-widget.js loader). Vanilla JS +
// Shadow DOM for style isolation, ActionCable for realtime with a polling
// fallback. Config comes from window.SupportWidgetSettings, mirroring the old
// window.intercomSettings convention.
import { createConsumer } from "@rails/actioncable";

(function () {
  if (window.__supportWidgetLoaded) return;
  window.__supportWidgetLoaded = true;

  var icons = {
    chat: '<svg viewBox="0 0 24 24" fill="currentColor"><path d="M12 3C6.48 3 2 6.94 2 11.8c0 2.2.9 4.2 2.4 5.74-.1 1.2-.5 2.6-1.4 3.86 1.7-.2 3.2-.8 4.3-1.5 1.4.6 3 .9 4.7.9 5.52 0 10-3.94 10-8.8S17.52 3 12 3z"/></svg>',
    close: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M6 6l12 12M18 6L6 18"/></svg>',
    send: '<svg viewBox="0 0 24 24" fill="currentColor"><path d="M3 20l18-8L3 4v6l12 2-12 2z"/></svg>'
  };

  var settings = window.SupportWidgetSettings || {};
  var endpoint = (settings.endpoint || window.location.origin).replace(/\/$/, "");
  var source = settings.source || "pro";
  var user = settings.user || null;
  var TOKEN_KEY = "support_widget_token";

  // ---- i18n ----------------------------------------------------------------
  // Locale comes from settings.locale (Прошка sets it from I18n.locale); on the
  // cross-origin landing it's usually absent, so fall back to the browser language.
  // Only ru/en are supported, defaulting to en (mirrors the Rails app).
  var DICT = {
    ru: {
      header: "Поддержка",
      prechat: "Оставьте контакты — мы ответим прямо здесь.",
      name: "Имя",
      email: "Email",
      start: "Начать чат",
      message: "Сообщение…",
      greeting: "👋 Здравствуйте! Чем могу помочь?",
      consent: "Согласен на обработку персональных данных",
      consentLink: "Политика",
      policyUrl: "https://stories-ar.com/ru/politika",
      ariaChat: "Чат",
      ariaClose: "Закрыть",
      ariaSend: "Отправить"
    },
    en: {
      header: "Support",
      prechat: "Leave your contacts — we'll reply right here.",
      name: "Name",
      email: "Email",
      start: "Start chat",
      message: "Message…",
      greeting: "👋 Hi! Can I help you today?",
      consent: "I consent to the processing of my personal data",
      consentLink: "Privacy policy",
      policyUrl: "https://stories-ar.com/en/privacy-policy",
      ariaChat: "Chat",
      ariaClose: "Close",
      ariaSend: "Send"
    }
  };
  function pickLocale() {
    var raw = (settings.locale || navigator.language || "en").toLowerCase();
    return raw.indexOf("ru") === 0 ? "ru" : "en";
  }
  var t = DICT[pickLocale()];
  // Ссылка на политику для чекбокса согласия в pre-chat. Прошка передаёт
  // links.privacy через SupportWidgetSettings; на лендинге — дефолт по языку.
  var policyUrl = settings.policyUrl || t.policyUrl;

  // ---- Proactive greeting (Intercom-style "Series") ------------------------
  // A teaser bubble pops above the launcher on a visitor's first visit, once.
  // Pure client-side: it's just a preview; replying opens the normal chat that
  // relays to Telegram. Configurable via settings:
  //   greeting:      false to disable, or a string to override the localized text
  //   greetingDelay: ms before the teaser appears (default 12s)
  var GREET_KEY = "support_widget_greeted";
  var greetingEnabled = settings.greeting !== false;
  var greetingText = (typeof settings.greeting === "string" && settings.greeting) || t.greeting;
  var greetingDelay = settings.greetingDelay != null ? settings.greetingDelay : 12000;

  var state = {
    token: localStorage.getItem(TOKEN_KEY) || null,
    open: false,
    needsProfile: false,
    connected: false,
    lastId: 0,
    seen: {},
    pollTimer: null,
    consumer: null,
    subscription: null,
    greetScheduled: false,
    pendingGreeting: null,
    unread: 0
  };

  // ---- Notification sound --------------------------------------------------
  // Synthesized via Web Audio (no asset to host / works cross-origin). Played
  // on incoming agent replies only. Browsers gate audio behind a user gesture,
  // so the context is resumed when the visitor opens the panel. Disable with
  // SupportWidgetSettings.sound = false.
  var audioCtx = null;

  function resumeAudio() {
    if (settings.sound === false) return;
    try {
      var Ctx = window.AudioContext || window.webkitAudioContext;
      if (!Ctx) return;
      audioCtx = audioCtx || new Ctx();
      if (audioCtx.state === "suspended") audioCtx.resume();
    } catch (e) {}
  }

  function playSound() {
    if (settings.sound === false || !audioCtx) return;
    try {
      var now = audioCtx.currentTime;
      [880, 1174.7].forEach(function (freq, i) { // A5 → D6, short chime
        var osc = audioCtx.createOscillator();
        var gain = audioCtx.createGain();
        osc.type = "sine";
        osc.frequency.value = freq;
        var start = now + i * 0.12;
        gain.gain.setValueAtTime(0.0001, start);
        gain.gain.exponentialRampToValueAtTime(0.16, start + 0.02);
        gain.gain.exponentialRampToValueAtTime(0.0001, start + 0.25);
        osc.connect(gain).connect(audioCtx.destination);
        osc.start(start);
        osc.stop(start + 0.3);
      });
    } catch (e) {}
  }

  // ---- DOM / Shadow root ---------------------------------------------------
  var el = {};

  function mount() {
    if (document.getElementById("support-widget-host")) return;
    state.open = false; // fresh DOM after a Turbolinks navigation starts closed
    var host = document.createElement("div");
    host.id = "support-widget-host";
    document.body.appendChild(host);
    var root = host.attachShadow({ mode: "open" });

    root.innerHTML =
      '<style>' + css() + '</style>' +
      '<div class="sw-teaser" hidden>' +
        '<button class="sw-teaser-close" aria-label="' + t.ariaClose + '">&times;</button>' +
        '<span class="sw-teaser-text"></span>' +
      '</div>' +
      '<button class="sw-bubble" aria-label="' + t.ariaChat + '">' + icons.chat + '<span class="sw-badge" hidden></span></button>' +
      '<div class="sw-panel" hidden>' +
        '<div class="sw-header"><span>' + t.header + '</span>' +
          '<button class="sw-close" aria-label="' + t.ariaClose + '">' + icons.close + '</button>' +
        '</div>' +
        '<div class="sw-prechat" hidden>' +
          '<p>' + t.prechat + '</p>' +
          '<input class="sw-name" type="text" placeholder="' + t.name + '" />' +
          '<input class="sw-email" type="email" placeholder="' + t.email + '" />' +
          // Посетитель лендинга нигде больше согласия не давал, а имя, email и
          // текст переписки уходят в Telegram — берём согласие здесь.
          '<label class="sw-consent"><input type="checkbox" class="sw-consent-box" /> ' +
            '<span>' + t.consent + ' (<a href="' + policyUrl + '" target="_blank" rel="noopener">' + t.consentLink + '</a>)</span>' +
          '</label>' +
          '<button class="sw-start" disabled>' + t.start + '</button>' +
        '</div>' +
        '<div class="sw-body" hidden><div class="sw-messages"></div></div>' +
        '<form class="sw-form" hidden>' +
          '<textarea class="sw-input" rows="1" placeholder="' + t.message + '"></textarea>' +
          '<button type="submit" class="sw-send" aria-label="' + t.ariaSend + '">' + icons.send + '</button>' +
        '</form>' +
      '</div>';

    el.bubble = root.querySelector(".sw-bubble");
    el.badge = root.querySelector(".sw-badge");
    el.teaser = root.querySelector(".sw-teaser");
    el.teaserText = root.querySelector(".sw-teaser-text");
    el.teaserClose = root.querySelector(".sw-teaser-close");
    el.panel = root.querySelector(".sw-panel");
    el.close = root.querySelector(".sw-close");
    el.prechat = root.querySelector(".sw-prechat");
    el.name = root.querySelector(".sw-name");
    el.email = root.querySelector(".sw-email");
    el.start = root.querySelector(".sw-start");
    el.consent = root.querySelector(".sw-consent-box");
    el.consent.addEventListener("change", function () {
      el.start.disabled = !el.consent.checked;
    });
    el.body = root.querySelector(".sw-body");
    el.messages = root.querySelector(".sw-messages");
    el.form = root.querySelector(".sw-form");
    el.input = root.querySelector(".sw-input");

    el.bubble.addEventListener("click", toggle);
    el.close.addEventListener("click", toggle);
    el.teaser.addEventListener("click", openFromTeaser);
    el.teaserClose.addEventListener("click", function (e) {
      e.stopPropagation();
      dismissTeaser();
    });
    el.start.addEventListener("click", startChat);
    el.form.addEventListener("submit", function (e) {
      e.preventDefault();
      sendMessage();
    });
    el.input.addEventListener("keydown", function (e) {
      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault();
        sendMessage();
      }
    });

    maybeGreet();
  }

  // Turbolinks replaces <body> on navigation, removing the injected host, so
  // re-mount on every turbolinks:load. mount() is idempotent (guards on the
  // host id), so the duplicate initial call is harmless. Falls back to the
  // standard ready events on sites without Turbolinks (e.g. the Tilda landing).
  document.addEventListener("turbolinks:load", mount);
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", mount);
  } else {
    mount();
  }

  function toggle() {
    state.open = !state.open;
    el.panel.hidden = !state.open;
    el.bubble.classList.toggle("sw-bubble--open", state.open);
    if (state.open) {
      resumeAudio(); // unlock audio on this user gesture
      dismissTeaser();
      clearUnread();
      openConversation();
    }
  }

  // ---- Unread badge --------------------------------------------------------
  function bumpUnread() {
    state.unread++;
    el.badge.textContent = state.unread > 9 ? "9+" : String(state.unread);
    el.badge.hidden = false;
  }

  function clearUnread() {
    state.unread = 0;
    if (el.badge) el.badge.hidden = true;
  }

  // ---- Proactive greeting --------------------------------------------------
  function maybeGreet() {
    if (!greetingEnabled || state.greetScheduled) return;
    if (localStorage.getItem(GREET_KEY)) return; // shown once per visitor
    if (state.token) return;                     // returning visitor, already engaged
    state.greetScheduled = true;
    setTimeout(function () {
      if (state.open || localStorage.getItem(GREET_KEY)) return;
      showTeaser();
    }, greetingDelay);
  }

  function showTeaser() {
    if (!el.teaser) return;
    el.teaserText.textContent = greetingText;
    el.teaser.hidden = false;
    el.teaser.classList.add("sw-teaser--in");
    localStorage.setItem(GREET_KEY, "1");
  }

  function dismissTeaser() {
    if (el.teaser) el.teaser.hidden = true;
  }

  function openFromTeaser() {
    dismissTeaser();
    state.pendingGreeting = greetingText; // shown as the opening agent message
    if (!state.open) toggle();
  }

  function appendLocalGreeting(text) {
    var row = document.createElement("div");
    row.className = "sw-msg sw-msg--agent";
    row.textContent = text;
    el.messages.appendChild(row);
    el.body.scrollTop = el.body.scrollHeight;
  }

  // ---- Conversation lifecycle ---------------------------------------------
  function openConversation() {
    api("POST", "/api/chat", { token: state.token, source: source })
      .then(function (data) {
        if (!data) return;
        state.token = data.token;
        localStorage.setItem(TOKEN_KEY, data.token);
        state.needsProfile = !!data.needs_profile;
        renderHistory(data.messages || []);
        if (state.needsProfile) {
          showPrechat();
        } else {
          showChat();
        }
        connect();
      });
  }

  function startChat() {
    var payload = {
      token: state.token,
      source: source,
      name: el.name.value.trim(),
      email: el.email.value.trim(),
      consent: el.consent && el.consent.checked ? "1" : "0"
    };
    api("POST", "/api/chat", payload).then(function (data) {
      if (!data) return;
      state.token = data.token;
      localStorage.setItem(TOKEN_KEY, data.token);
      state.needsProfile = false;
      showChat();
      connect();
    });
  }

  function sendMessage() {
    var body = el.input.value.trim();
    if (!body || !state.token) return;
    el.input.value = "";
    api("POST", "/api/chat/" + state.token + "/messages", {
      body: body,
      name: el.name.value.trim(),
      email: el.email.value.trim()
    }).then(function (data) {
      if (data && data.message) appendMessage(data.message);
    });
  }

  // ---- Realtime + polling fallback ----------------------------------------
  function connect() {
    if (state.consumer) return;
    try {
      var wsBase = endpoint.replace(/^http/, "ws");
      state.consumer = createConsumer(wsBase + "/cable?token=" + encodeURIComponent(state.token));
      state.subscription = state.consumer.subscriptions.create(
        { channel: "ConversationChannel", token: state.token },
        {
          connected: function () { state.connected = true; },
          disconnected: function () { state.connected = false; startPolling(); },
          received: function (data) { if (data && data.message) appendMessage(data.message); }
        }
      );
    } catch (e) {
      startPolling();
    }
    // If the socket doesn't connect quickly, fall back to polling.
    setTimeout(function () { if (!state.connected) startPolling(); }, 4000);
  }

  function startPolling() {
    if (state.pollTimer || !state.token) return;
    state.pollTimer = setInterval(function () {
      api("GET", "/api/chat/" + state.token + "/messages?after=" + state.lastId).then(function (data) {
        if (data && data.messages) data.messages.forEach(function (m) { appendMessage(m); });
      });
    }, 4000);
  }

  // ---- Rendering -----------------------------------------------------------
  function renderHistory(messages) {
    el.messages.innerHTML = "";
    state.seen = {};
    state.lastId = 0;
    messages.forEach(function (m) { appendMessage(m, true); }); // silent: no chime on history
  }

  function appendMessage(msg, silent) {
    if (!msg || state.seen[msg.id]) return;
    state.seen[msg.id] = true;
    if (msg.id > state.lastId) state.lastId = msg.id;
    var row = document.createElement("div");
    row.className = "sw-msg sw-msg--" + (msg.sender === "agent" ? "agent" : msg.sender === "system" ? "system" : "visitor");
    row.textContent = msg.body;
    el.messages.appendChild(row);
    el.body.scrollTop = el.body.scrollHeight;
    if (!silent && msg.sender === "agent") {
      playSound();
      if (!state.open) bumpUnread();
    }
  }

  function showPrechat() {
    el.prechat.hidden = false;
    el.body.hidden = true;
    el.form.hidden = true;
  }

  function showChat() {
    el.prechat.hidden = true;
    el.body.hidden = false;
    el.form.hidden = false;
    if (state.pendingGreeting) {
      appendLocalGreeting(state.pendingGreeting);
      state.pendingGreeting = null;
    }
    el.input.focus();
  }

  // ---- HTTP ----------------------------------------------------------------
  function api(method, path, body) {
    var opts = {
      method: method,
      headers: { "Accept": "application/json" },
      credentials: "same-origin"
    };
    if (body && method !== "GET") {
      opts.headers["Content-Type"] = "application/json";
      opts.body = JSON.stringify(body);
    }
    return fetch(endpoint + path, opts)
      .then(function (r) { return r.ok ? r.json() : null; })
      .catch(function () { return null; });
  }

  function css() {
    // Brand palette mirrors the Rails app (app/javascript/stylesheets/lib/_variables.scss):
    // $primary #FFAF16 amber, CTA gradient #FFA703→#FFC14B, $dark #262626 text.
    var brand = "#FFAF16";
    var brandGrad = "linear-gradient(324.93deg, #FFA703 0.74%, #FFC14B 58.95%)";
    var dark = "#262626";
    return [
      ":host{all:initial}",
      // author display rules below would otherwise beat the UA [hidden] rule,
      // so make hidden explicit and authoritative inside the shadow tree.
      "[hidden]{display:none!important}",
      "*{box-sizing:border-box;font-family:-apple-system,Segoe UI,Roboto,Helvetica,Arial,sans-serif}",
      ".sw-bubble{position:fixed;right:20px;bottom:20px;width:56px;height:56px;border:none;border-radius:50%;background:" + brandGrad + ";color:#fff;cursor:pointer;box-shadow:0 6px 20px rgba(0,0,0,.25);display:flex;align-items:center;justify-content:center;z-index:2147483000}",
      ".sw-bubble svg{width:26px;height:26px}",
      ".sw-badge{position:absolute;top:-2px;right:-2px;min-width:20px;height:20px;padding:0 5px;border-radius:10px;background:#E84347;color:#fff;font-size:11px;font-weight:700;line-height:20px;text-align:center;box-shadow:0 0 0 2px #fff}",
      ".sw-teaser{position:fixed;right:20px;bottom:88px;max-width:260px;background:#fff;color:" + dark + ";padding:13px 32px 13px 14px;border-radius:14px;box-shadow:0 8px 24px rgba(0,0,0,.18);font-size:14px;line-height:1.4;cursor:pointer;z-index:2147483000}",
      ".sw-teaser--in{animation:sw-pop .25s ease}",
      ".sw-teaser-close{position:absolute;top:6px;right:9px;background:none;border:none;font-size:17px;line-height:1;color:#aaa;cursor:pointer;padding:0}",
      "@keyframes sw-pop{from{opacity:0;transform:translateY(8px)}to{opacity:1;transform:none}}",
      ".sw-panel{position:fixed;right:20px;bottom:88px;width:360px;max-width:calc(100vw - 32px);height:520px;max-height:calc(100vh - 120px);background:#fff;border-radius:16px;box-shadow:0 12px 40px rgba(0,0,0,.28);display:flex;flex-direction:column;overflow:hidden;z-index:2147483000}",
      ".sw-header{background:" + brandGrad + ";color:" + dark + ";padding:14px 16px;font-weight:600;display:flex;align-items:center;justify-content:space-between}",
      ".sw-close{background:none;border:none;color:" + dark + ";cursor:pointer;padding:0}",
      ".sw-close svg{width:18px;height:18px}",
      ".sw-prechat{padding:16px;display:flex;flex-direction:column;gap:10px}",
      ".sw-prechat p{margin:0 0 4px;color:#444;font-size:14px}",
      ".sw-prechat input{padding:10px 12px;border:1px solid #d1d1d1;border-radius:10px;font-size:14px}",
      ".sw-prechat input:focus{outline:none;border-color:" + brand + "}",
      ".sw-prechat .sw-start{padding:10px 12px;border:none;border-radius:10px;background:" + brandGrad + ";color:" + dark + ";font-weight:600;cursor:pointer;min-height:44px}",
      ".sw-prechat .sw-start:disabled{opacity:.45;cursor:not-allowed}",
      ".sw-consent{display:flex;gap:8px;align-items:flex-start;font-size:12px;line-height:1.4;opacity:.85;cursor:pointer;min-height:44px}",
      ".sw-consent input{margin-top:2px;width:16px;height:16px;flex:none}",
      ".sw-consent a{color:inherit;text-decoration:underline}",
      ".sw-body{flex:1;overflow-y:auto;padding:14px;background:#F6F6F6}",
      ".sw-msg{max-width:80%;padding:9px 12px;border-radius:14px;margin-bottom:8px;font-size:14px;line-height:1.35;white-space:pre-wrap;word-wrap:break-word}",
      ".sw-msg--visitor{margin-left:auto;background:" + brand + ";color:" + dark + ";border-bottom-right-radius:4px}",
      ".sw-msg--agent{margin-right:auto;background:#fff;color:" + dark + ";border:1px solid #ececec;border-bottom-left-radius:4px}",
      ".sw-msg--system{margin:0 auto 8px;background:transparent;color:#888;font-size:12px;text-align:center}",
      ".sw-form{display:flex;align-items:flex-end;gap:8px;padding:10px;border-top:1px solid #eee}",
      ".sw-input{flex:1;resize:none;border:1px solid #d1d1d1;border-radius:12px;padding:10px 12px;font-size:14px;max-height:120px}",
      ".sw-input:focus{outline:none;border-color:" + brand + "}",
      ".sw-send{border:none;background:" + brandGrad + ";color:" + dark + ";border-radius:50%;width:40px;height:40px;cursor:pointer;display:flex;align-items:center;justify-content:center;flex:0 0 auto}",
      ".sw-send svg{width:18px;height:18px}"
    ].join("");
  }

})();
